/**
 * COMPONENTE LIVE PRICES SECTION
 * ===============================
 * Muestra la tabla completa de precios de remates
 * Ahora obtiene los datos del backend
 */

import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { api } from '../../services/api';

// Mapeo jerárquico de categorías (Cat0 > Cat1 > Categorías de BD)
const CATEGORY_MAPPING = {
  'Invernada': {
    'Terneros': [
      'Terneros', 'Terneros - 140 kg', 'Terneros -140 kg',
      'Terneros entre 140 y 180 kg', 'Terneros + 180 kg',
      'Terneros / Terneras', 'Terneros/Terneras'
    ],
    'Novillos': [
      'Novillos 1-2 años', 'Novillos 1 -2 años', 'Novillos 1- 2 años',
      'Novillos 2-3 años', 'Novillos 2 - 3 años',
      'Novillos +3 años', 'Novillos + 3 años'
    ],
    'Holandos': ['Holandos', 'Holando'],
    'Vacas': ['Vacas de Invernada'],
    'Enteros': ['Mas de 1 año enteros', 'Mas de un año enteros'],
    'Mixtos': ['Mixto mas de 1 año', 'Mixtos + de 1 año', 'Mixto + de 1 año']
  },
  'Cría': {
    'Terneras': ['Terneras'],
    'Vaquillonas': [
      'Vaquillonas 1-2 años', 'Vaquillonas 1 - 2 años',
      'Vaquillonas + 2 años'
    ],
    'Vientres': ['Vientres Preñados', 'Vientres Entorados'],
    'Vacas': ['Vacas de Cría', 'Vaca de Cría', 'Vacas de Pedrigree'],
    'Piezas de Cría': ['Piezas de Cria']
  },
  'Lanares': {
    'Corderos': ['Corderos y Corderas', 'Corderos dl', 'Corderas dl', 'Corderos / Corderas'],
    'Ovejas': [
      'Oveja de cría 2 o + enc.', 'Oveja de Cría 2 o + enc', 'Oveja de cría o + enc',
      'Oveja de cría última enc.', 'Oveja de invernada', 'Ovejas de invernada'
    ],
    'Borregas': ['Borregas 2 di', 'Borregas 2 dtes', 'Borregas 4-6 di', 'Borregas 4-6 dtes', 'Borregos 2 dtes'],
    'Capones': ['Capones 2-4 di', 'Capones 4-6 di', 'Capones 4-6 dtes', 'Capones BLL'],
    'Piezas Ovinas': ['Piezas de cría (OV)', 'Pieza de Cría (OV)']
  },
  'Toro': {
    'Toros': ['Toros']
  }
};

// Obtener todas las categorías de un Cat0
const getAllCategoriesForCat0 = (cat0) => {
  if (!cat0 || !CATEGORY_MAPPING[cat0]) return [];
  return Object.values(CATEGORY_MAPPING[cat0]).flat();
};

// Obtener categorías de un Cat1 específico
const getCategoriesForCat1 = (cat0, cat1) => {
  if (!cat0 || !cat1 || !CATEGORY_MAPPING[cat0] || !CATEGORY_MAPPING[cat0][cat1]) return [];
  return CATEGORY_MAPPING[cat0][cat1];
};

// Verificar si un Cat0 tiene datos en los promedios actuales
const cat0HasData = (cat0, data) => {
  if (!data || data.length === 0) return false;
  const allCategories = getAllCategoriesForCat0(cat0);
  return data.some(item => allCategories.includes(item.categoria));
};

// Verificar si un Cat1 tiene datos en los promedios actuales
const cat1HasData = (cat0, cat1, data) => {
  if (!data || data.length === 0) return false;
  const categories = getCategoriesForCat1(cat0, cat1);
  return data.some(item => categories.includes(item.categoria));
};

const LivePricesSection = () => {
  const [selectedCat0, setSelectedCat0] = useState(null); // null = Todos
  const [selectedCat1, setSelectedCat1] = useState(null);
  const [visibleRows, setVisibleRows] = useState([]);
  const [rematesData, setRematesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rematesList, setRematesList] = useState([]);
  const [selectedRemate, setSelectedRemate] = useState('');

  // Cargar lista de remates disponibles y preseleccionar el primero con datos
  useEffect(() => {
    const fetchRematesListAndSelectFirst = async () => {
      try {
        const data = await api.getRematesList();
        console.log('[LivePricesSection] Lista de remates cargada:', data);
        setRematesList(data);

        // Encontrar el primer remate que tenga datos de promedios
        if (data.length > 0 && !selectedRemate) {
          // Buscar el primer remate que tenga la bandera has_data = true
          const remateWithData = data.find(r => r.has_data);

          if (remateWithData) {
            console.log('[LivePricesSection] Primer remate con datos (flag):', remateWithData.numero);
            setSelectedRemate(remateWithData.numero);
          } else {
            // Si ninguno tiene datos, fallback al primero
            console.log('[LivePricesSection] Ningún remate tiene datos, seleccionando el primero:', data[0].numero);
            setSelectedRemate(data[0].numero);
          }
        }
      } catch (error) {
        console.error('[LivePricesSection] Error cargando lista de remates:', error);
      }
    };

    // Solo ejecutar si no hay remate seleccionado
    if (!selectedRemate) {
      fetchRematesListAndSelectFirst();
    }
  }, []);


  // Cargar datos del remate seleccionado
  useEffect(() => {
    const fetchPromedios = async () => {
      if (!selectedRemate) {
        console.log('[LivePricesSection] No hay remate seleccionado');
        return;
      }

      console.log('[LivePricesSection] Cargando promedios para remate:', selectedRemate);
      setLoading(true);
      setVisibleRows([]); // Reset animación

      try {
        const data = await api.getPromediosRemate(selectedRemate);
        console.log('[LivePricesSection] Promedios cargados:', data.length, 'items');
        setRematesData(data);

        // Animación de entrada escalonada (30ms por fila - más rápido)
        data.forEach((_, index) => {
          setTimeout(() => {
            setVisibleRows(prev => [...prev, index]);
          }, index * 30);
        });
      } catch (error) {
        console.error('Error loading promedios:', error);
        setRematesData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPromedios();
  }, [selectedRemate]);

  // Handler para cambio del selector de remate
  const handleRemateChange = (e) => {
    const newValue = e.target.value;
    console.log('[LivePricesSection] Usuario cambió remate de', selectedRemate, 'a', newValue);
    setSelectedRemate(Number(newValue));
    setSelectedCat0(null);
    setSelectedCat1(null);
  };

  // Handler para cambio de Cat0
  const handleCat0Change = (cat0) => {
    console.log('[LivePricesSection] Cat0 seleccionado:', cat0);
    if (cat0 === 'todos') {
      setSelectedCat0(null);
      setSelectedCat1(null);
    } else {
      setSelectedCat0(cat0);
      setSelectedCat1(null); // Reset Cat1 al cambiar Cat0
    }
  };

  // Handler para cambio de Cat1
  const handleCat1Change = (cat1) => {
    console.log('[LivePricesSection] Cat1 seleccionado:', cat1);
    if (cat1 === 'todos') {
      setSelectedCat1(null);
    } else {
      setSelectedCat1(cat1);
    }
  };

  // Filtrar datos según las categorías seleccionadas
  const filteredData = rematesData.filter(item => {
    // Si no hay Cat0 seleccionado, mostrar todo
    if (!selectedCat0) return true;

    const categoria = item.categoria;

    // Si hay Cat0 pero no Cat1, mostrar todas las categorías del Cat0
    if (!selectedCat1) {
      const allCategories = getAllCategoriesForCat0(selectedCat0);
      return allCategories.includes(categoria);
    }

    // Si hay Cat0 y Cat1, mostrar solo las categorías del Cat1
    const categories = getCategoriesForCat1(selectedCat0, selectedCat1);
    return categories.includes(categoria);
  });

  console.log('[LivePricesSection] Filtros - Cat0:', selectedCat0, 'Cat1:', selectedCat1);
  console.log('[LivePricesSection] Datos filtrados:', filteredData.length, 'de', rematesData.length);

  return (
    <section id="precios" className="py-20 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-green-100 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-100 rounded-full blur-3xl opacity-30"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
              Precios en Tiempo Real
            </span>
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            Monitor de
            <span className="gradient-text"> Precios</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Consulta los precios actualizados de los últimos remates
          </p>
        </div>

        {/* Selector de Remate y Filtros */}
        <div className="flex flex-col gap-4 mb-8">
          {/* Fila 1: Selector de Remate + Filtros Cat0 */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-gray-700">Remate:</label>
              <select
                className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold text-gray-700 focus:border-green-500 focus:outline-none transition-all duration-300 cursor-pointer"
                value={selectedRemate || ''}
                onChange={handleRemateChange}
              >
                {rematesList.map((remate) => (
                  <option key={remate.id} value={remate.numero}>
                    R{remate.numero}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Botón Todos */}
              <button
                onClick={() => handleCat0Change('todos')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${!selectedCat0
                  ? 'bg-gradient-to-r from-green-600 to-green-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
              >
                Todos
              </button>
              {/* Botones Cat0 - solo mostrar si tienen datos */}
              {Object.keys(CATEGORY_MAPPING)
                .filter((cat0) => cat0HasData(cat0, rematesData))
                .map((cat0) => {
                  const isActive = selectedCat0 === cat0;
                  return (
                    <button
                      key={cat0}
                      onClick={() => handleCat0Change(cat0)}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${isActive
                        ? 'bg-gradient-to-r from-green-600 to-green-500 text-white shadow-lg'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                        }`}
                    >
                      {cat0}
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Fila 2: Filtros Cat1 (solo si hay Cat0 seleccionado) */}
          {selectedCat0 && CATEGORY_MAPPING[selectedCat0] && (
            <div className="flex flex-wrap justify-center gap-2 animate-fade-in">
              {/* Botón Todos Cat1 */}
              <button
                onClick={() => handleCat1Change('todos')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 ${!selectedCat1
                  ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                  }`}
              >
                Ver todos
              </button>
              {/* Botones Cat1 - solo mostrar si tienen datos */}
              {Object.keys(CATEGORY_MAPPING[selectedCat0])
                .filter((cat1) => cat1HasData(selectedCat0, cat1, rematesData))
                .map((cat1) => {
                  const isActive = selectedCat1 === cat1;
                  return (
                    <button
                      key={cat1}
                      onClick={() => handleCat1Change(cat1)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300 ${isActive
                        ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                        }`}
                    >
                      {cat1}
                    </button>
                  );
                })}
            </div>
          )}
        </div>

        {/* Prices Grid - Mobile Compact Cards */}
        <div className="lg:hidden space-y-3">
          {filteredData.slice(0, 6).map((item) => {
            const originalIndex = rematesData.indexOf(item);
            return (
              <Card
                key={originalIndex}
                className={`transform transition-all duration-500 hover:scale-102 border-l-4 ${item.varPercent >= 0 ? 'border-l-green-500' : 'border-l-red-500'
                  } ${visibleRows.includes(originalIndex) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-sm">{item.categoria}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {item.cabeza} cab · {item.lote} lotes
                      </p>
                    </div>
                    {/* Indicador de variación */}
                    <div className="flex items-center justify-end">
                      {item.pBultoAnterior === 0 ? (
                        // No hay datos del remate anterior
                        <span className="text-xs text-gray-400 font-medium">N/A</span>
                      ) : item.varPercent === 0 ? (
                        // Precio se mantuvo igual
                        <div className="flex items-center space-x-1 text-xs font-bold text-gray-500">
                          <Minus className="w-4 h-4" />
                          <span>=</span>
                        </div>
                      ) : item.varPercent > 0 ? (
                        // Precio subió
                        <div className="flex items-center space-x-1 text-xs font-bold text-green-600">
                          <TrendingUp className="w-4 h-4" />
                          <span>+{item.varPercent.toFixed(1)}%</span>
                        </div>
                      ) : (
                        // Precio bajó
                        <div className="flex items-center space-x-1 text-xs font-bold text-red-600">
                          <TrendingDown className="w-4 h-4" />
                          <span>{item.varPercent.toFixed(1)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Prom</p>
                      <p className="text-sm font-bold text-gray-900">${item.prom.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">P.Bulto</p>
                      <p className="text-sm font-bold text-green-600">${item.pBulto.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Máx</p>
                      <p className="text-sm font-semibold text-gray-700">${item.max.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Mín</p>
                      <p className="text-sm font-semibold text-gray-700">${item.min.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Desktop Table - Compacta */}
        <div className="hidden lg:block">
          <Card className="overflow-hidden shadow-2xl border-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-green-600 to-green-500 text-white">
                    <th className="px-3 py-2 text-left text-xs font-bold uppercase">Categoría</th>
                    <th className="px-3 py-2 text-center text-xs font-bold uppercase">Cab.</th>
                    <th className="px-3 py-2 text-center text-xs font-bold uppercase">Lot.</th>
                    <th className="px-3 py-2 text-center text-xs font-bold uppercase">Máx</th>
                    <th className="px-3 py-2 text-center text-xs font-bold uppercase">Mín</th>
                    <th className="px-3 py-2 text-center text-xs font-bold uppercase">Prom</th>
                    <th className="px-3 py-2 text-center text-xs font-bold uppercase">P.Bulto</th>
                    <th className="px-3 py-2 text-center text-xs font-bold uppercase">Var %</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredData.map((item) => {
                    const originalIndex = rematesData.indexOf(item);
                    return (
                      <tr
                        key={originalIndex}
                        className={`border-b border-gray-100 hover:bg-green-50 transition-all duration-200 cursor-pointer ${visibleRows.includes(originalIndex) ? 'opacity-100' : 'opacity-0'
                          }`}
                        style={{ transitionDelay: `${originalIndex * 30}ms` }}
                      >
                        <td className="px-3 py-2 text-sm font-semibold text-gray-900">{item.categoria}</td>
                        <td className="px-3 py-2 text-center text-sm text-gray-700">{item.cabeza.toLocaleString()}</td>
                        <td className="px-3 py-2 text-center text-sm text-gray-700">{item.lote}</td>
                        <td className="px-3 py-2 text-center text-sm text-gray-700">${item.max.toFixed(2)}</td>
                        <td className="px-3 py-2 text-center text-sm text-gray-700">${item.min.toFixed(2)}</td>
                        <td className="px-3 py-2 text-center text-sm font-bold text-gray-900">${item.prom.toFixed(2)}</td>
                        <td className="px-3 py-2 text-center text-sm font-bold text-green-600">${item.pBulto.toFixed(2)}</td>
                        {/* Columna de variación */}
                        <td className="px-3 py-2 text-center">
                          {item.pBultoAnterior === 0 ? (
                            // No hay datos del remate anterior
                            <span className="text-xs text-gray-400 font-medium">N/A</span>
                          ) : item.varPercent === 0 ? (
                            // Precio se mantuvo igual
                            <span className="inline-flex items-center space-x-1 text-xs font-bold text-gray-500">
                              <Minus className="w-3 h-3" />
                              <span>=</span>
                            </span>
                          ) : item.varPercent > 0 ? (
                            // Precio subió - verde
                            <span className="inline-flex items-center space-x-1 text-xs font-bold text-green-600">
                              <TrendingUp className="w-3 h-3" />
                              <span>+{item.varPercent.toFixed(1)}%</span>
                            </span>
                          ) : (
                            // Precio bajó - rojo
                            <span className="inline-flex items-center space-x-1 text-xs font-bold text-red-600">
                              <TrendingDown className="w-3 h-3" />
                              <span>{item.varPercent.toFixed(1)}%</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default LivePricesSection;